/* ----------------- External --------------- */
import { Box, TooltipWrapper } from "@memorang/ui";
import { Caption } from "react-native-paper";

/* ----------------- Internal --------------- */
import {
	DEFAULT_MAX_EMOTIONS,
	EMOTIONS,
	type Emotion,
} from "@features/session/constants/emotions";
import { useEffect } from "react";

/* ----------------- Types --------------- */
interface EmotionPrediction {
	emotions: { name: string; score: number }[];
}

interface EmotionScoreDisplayProps {
	predictions: EmotionPrediction[];
	maxEmotions?: number;
}

const CircularProgress = ({
	score,
	emoji,
	color,
}: {
	score: number;
	emoji: string;
	color: string;
}) => {
	const radius = 36;
	const circumference = 2 * Math.PI * radius;
	const strokeDasharray = `${(score * circumference) / 100} ${circumference}`;

	return (
		<Box position="relative" width={80} height={80}>
			<svg width="80" height="80" style={{ transform: "rotate(-90deg)" }}>
				<circle
					cx="40"
					cy="40"
					r={radius}
					stroke={`${color}20`}
					strokeWidth="4"
					fill="none"
				/>
				<circle
					cx="40"
					cy="40"
					r={radius}
					stroke={color}
					strokeWidth="4"
					fill="none"
					strokeLinecap="round"
					strokeDasharray={strokeDasharray}
					style={{ transition: "stroke-dasharray 0.5s ease-in-out" }}
				/>
			</svg>
			<Box
				position="absolute"
				top={40}
				left={40}
				style={{
					transform: "translate(-50%, -50%)",
				}}
				alignItems="center"
			>
				<Caption style={{ fontSize: 32 }}>{emoji}</Caption>
			</Box>
		</Box>
	);
};

/* ----------------- Component --------------- */
const EmotionScoreDisplay = ({
	predictions,
	maxEmotions = DEFAULT_MAX_EMOTIONS,
}: EmotionScoreDisplayProps) => {
	useEffect(() => {
		if (!predictions?.length) {
			return;
		}
	}, [predictions]);

	// Get the latest prediction's emotions and sort by score
	const topEmotions = predictions[predictions.length - 1].emotions
		.sort((a, b) => b.score - a.score)
		.slice(0, maxEmotions)
		.map((emotion) => {
			const emotionData = EMOTIONS[emotion.name.toLowerCase()];
			if (!emotionData) {
				return null;
			}

			return {
				...emotion,
				...emotionData,
			};
		})
		.filter(
			(emotion): emotion is Emotion & { score: number } => emotion !== null,
		);

	if (!topEmotions.length) {
		return null;
	}

	return (
		<Box padding={24}>
			<Box flexDirection="row" justifyContent="center" flexWrap="wrap" gap={32}>
				{topEmotions.map(({ name, score, emoji, color, description }) => (
					<Box
						key={name}
						alignItems="center"
						width={80}
						// marginHorizontal={4}
					>
						<TooltipWrapper title={description}>
							<CircularProgress
								score={score * 100}
								emoji={emoji}
								color={color}
							/>
						</TooltipWrapper>
						<Caption
							style={{
								marginTop: 8,
								textAlign: "center",
								fontSize: 12,
							}}
						>
							{name}
						</Caption>
					</Box>
				))}
			</Box>
		</Box>
	);
};

export default EmotionScoreDisplay;
